// @flow
import React, {Component, Fragment} from 'react';
import FilteredCountDataProvider from '../FilteredCountDataProvider/FilteredCountDataProvider';
import {triggerEdapEventForCheckbox} from '../../utils/filtersEdapUtils';
import {connect} from 'react-redux';
import searchParamsActions from '../../actions/searchParams';
import {newArrayFromFilters} from '../../utils/filters';
import FilterGroup from '../FilterGroup/FilterGroup';
import {FilterBarDropdown} from '../FilterBar/FilterBar';
import {DropdownControlled} from '@homeaway/react-dropdown';
import DropdownPanel from '../DropdownPanel/DropdownPanel';
import {FormattedMessage} from 'react-intl';
import Experiment from '@homeaway/react-experiment';

import {HA_FAB_FILTER_POPULAR_GROUP} from '../../abacus/experiments';

import './PopularFiltersDropdown.less';

import type {State as ReduxState} from '../../reducers/index';
import type {SearchParamsType} from '../../reducers/searchParams';

export const POPULAR_FILTERS_GROUP_ID = '13';

type DispatchProps = {
    updateInputParams: typeof searchParamsActions.updateInputParams,
    updateFilterCheck: typeof searchParamsActions.updateFilterCheck
};

type StateProps = {
    inputParams: SearchParamsType,
    q: ?string
};

type OwnProps = {
    searchResultsLoading: boolean,
    id: string,
    onDropdownClick: () => void,
    onApply: (event: SyntheticEvent<HTMLInputElement>) => void,
    open: boolean
};

type Props = OwnProps & DispatchProps & StateProps;

export const secondaryLabel = (q: ?string) => (
    <FormattedMessage
        id="popularFiltersSecondaryLabel"
        defaultMessage="These filters are popular in {DESTINATION, select, undefined{this area} other{{DESTINATION}}}."
        values={{DESTINATION: q}}
    >
        {(txt) => <Fragment>{txt}</Fragment>}
    </FormattedMessage>
);

export const getPopularFiltersGroup = (filterGroups: *) =>
    filterGroups.filter(
        ({groupInfo: {id}}) => id === POPULAR_FILTERS_GROUP_ID
    )[0];

export class PopularFiltersDropdown extends Component<Props> {
    handleChecked = (event: SyntheticEvent<HTMLInputElement>) => {
        if (!event || !event.currentTarget) {
            return;
        }

        triggerEdapEventForCheckbox(event);

        this.props.updateFilterCheck(event.currentTarget);
    };

    handleClear = (popularFilterGroup: *) => () => {
        const {filter = []} = this.props.inputParams;
        const popularFilters = popularFilterGroup.filters.map(
            (searchFilter) => searchFilter.filter.refineByQueryArgument
        );
        this.props.updateInputParams({
            filter: filter.filter((f) => !popularFilters.includes(f))
        });
    };

    render() {
        const {
            searchResultsLoading,
            inputParams,
            id,
            onDropdownClick,
            onApply,
            open,
            q
        } = this.props;
        return (
            <Experiment
                name={HA_FAB_FILTER_POPULAR_GROUP}
                variant={Experiment.ON}
            >
                <FilteredCountDataProvider
                    searchResultsLoading={searchResultsLoading}
                >
                    {({filterGroups}) => {
                        const filterGroup = getPopularFiltersGroup(
                            filterGroups
                        );

                        if (!filterGroup) {
                            return null;
                        }

                        const selectedCount = filterGroup.filters.filter(
                            ({checked}) => checked
                        ).length;

                        return (
                            <FilterBarDropdown
                                dropdownClass="PopularFiltersDropdown"
                                dataWdio="PopularFiltersDropdown"
                                isSelected={selectedCount > 0}
                            >
                                <DropdownControlled
                                    id={id}
                                    label={
                                        filterGroup.groupInfo.name +
                                        (selectedCount > 0
                                            ? ` (${selectedCount})`
                                            : '')
                                    }
                                    onDropdownClick={onDropdownClick}
                                    onBlur={onApply}
                                    onTouch={onApply}
                                    open={open}
                                >
                                    {open && (
                                        <DropdownPanel
                                            showButtons
                                            clearVisible
                                            clearLabel="popularFiltersClear"
                                            onApply={onApply}
                                            onClear={this.handleClear(
                                                filterGroup
                                            )}
                                        >
                                            <div
                                                className="PopularFiltersDropdown__filterGroup"
                                                key={filterGroup.groupInfo.id}
                                                data-wdio={
                                                    'PopularFiltersDropdown__filterGroup'
                                                }
                                            >
                                                <FilterGroup
                                                    secondaryLabel={secondaryLabel(
                                                        q
                                                    )}
                                                    handleChecked={
                                                        this.handleChecked
                                                    }
                                                    groupName={
                                                        filterGroup.groupInfo
                                                            .name
                                                    }
                                                    filterGroupId={
                                                        filterGroup.groupInfo.id
                                                    }
                                                    filters={
                                                        filterGroup.filters
                                                    }
                                                    checked={newArrayFromFilters(
                                                        inputParams
                                                    )}
                                                    tooltipOptions={{
                                                        isMobile: false
                                                    }}
                                                />
                                            </div>
                                        </DropdownPanel>
                                    )}
                                </DropdownControlled>
                            </FilterBarDropdown>
                        );
                    }}
                </FilteredCountDataProvider>
            </Experiment>
        );
    }
}

export const mapStateToProps = (state: ReduxState): StateProps => {
    const {searchParams} = state;
    const {
        inputParams,
        params: {q}
    } = searchParams;
    return {
        inputParams,
        q
    };
};

const mapDispatchToProps = {
    updateInputParams: searchParamsActions.updateInputParams,
    updateFilterCheck: searchParamsActions.updateFilterCheck
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(PopularFiltersDropdown);
